/**
 * Mobile Menu Component
 *
 * Manages the slide-in mobile navigation panel.
 * Listens for 'open-mobile-menu' custom event (dispatched from master.js).
 *
 * Usage in HTML:
 *   <div data-x-data="mobileMenu"> ... </div>
 *
 * Exposed state:
 *   - open (boolean): Whether the menu is currently visible
 *
 * Exposed methods:
 *   - show(): Opens the menu and traps focus
 *   - hide(): Closes the menu and restores focus
 *   - toggle(): Toggles the menu open/closed
 */
export default function mobileMenu() {
    return {
        open: false,
        _previousFocus: null,
        _scrollY: 0,

        init() {
            // Listen for the custom event dispatched by hamburger button in master.js
            this._onOpen = () => this.show();
            window.addEventListener('open-mobile-menu', this._onOpen);
        },

        destroy() {
            window.removeEventListener('open-mobile-menu', this._onOpen);
            this._unlockScroll();
        },

        show() {
            this._previousFocus = document.activeElement;
            this.open = true;
            this._lockScroll();

            // Move focus into the panel after transition
            this.$nextTick(() => {
                const closeBtn = this.$el.querySelector('[data-menu-close]');
                if (closeBtn) closeBtn.focus();
            });
        },

        hide() {
            this.open = false;
            this._unlockScroll();

            // Restore focus to the element that opened the menu
            if (this._previousFocus) {
                this.$nextTick(() => {
                    this._previousFocus.focus();
                    this._previousFocus = null;
                });
            }
        },

        // Pins the page in place while the menu is open so background touch-scrolling
        // can't trigger the browser's address bar to hide/show (which leaves a gap
        // under the full-screen panel on Android).
        _lockScroll() {
            this._scrollY = window.scrollY || window.pageYOffset || 0;
            document.documentElement.classList.add('scroll-locked');
            document.documentElement.style.top = `-${this._scrollY}px`;
        },

        _unlockScroll() {
            if (!document.documentElement.classList.contains('scroll-locked')) return;
            document.documentElement.classList.remove('scroll-locked');
            document.documentElement.style.top = '';
            window.scrollTo(0, this._scrollY);
        },

        toggle() {
            this.open ? this.hide() : this.show();
        }
    };
}
